// @ts-strict-ignore
import { APP_BASE_HREF } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { Data } from '@angular/router';
import { createSelector, Store } from '@ngrx/store';
import { slashJoin, withLeadingSlash } from 'path-slashes';
import { combineLatest, Observable, of } from 'rxjs';
import { first, map } from 'rxjs/operators';

import { AppRoutePathParam } from 'src/app/routing/app-route-path-param.enum';
import { AppRoutePath } from 'src/app/routing/app-route-path.enum';
import {
  selectCountry,
  selectLanguage,
  selectOnlineCode,
  selectSubprocess,
  selectVehicleBody,
  selectVehicleClass,
  selectVehicleId
} from 'src/app/store/runtime-variables/runtime-variables.selectors';
import { filterTruthy } from 'src/app/utils/functions/rx-filters';
import { URL_PATH_SUMMARY } from 'src/app/utils/global-constants';
import { WINDOW } from 'src/app/utils/window.token';

// eslint-disable-next-line @ngrx/prefix-selectors-with-select
const ROUTE_PATH_PARAM_TO_SELECTOR_MAP = {
  [AppRoutePathParam.Subprocess]: selectSubprocess,
  [AppRoutePathParam.Country]: selectCountry,
  [AppRoutePathParam.Language]: selectLanguage,
  [AppRoutePathParam.VehicleClass]: selectVehicleClass,
  [AppRoutePathParam.VehicleBody]: selectVehicleBody,
  [AppRoutePathParam.VehicleId]: selectVehicleId,
  [AppRoutePathParam.OnlineCode]: selectOnlineCode,
  [AppRoutePathParam.BM]: createSelector(() => 'bm')
};

/**
 * The LinkService generates url strings or arrays to link to internal app
 * routes with their specific path parameters.
 *
 * For all methods, setting explicit path parameters is OPTIONAL.
 *
 * If not explicitly set, path parameters will be taken from the current app
 * context state.
 *
 * E.g.: Using `.toMotorization()` within Configuration context, will
 * automatically use VehicleClass and VehicleBody from the currently configured
 * vehicle as path params to link to motorization page.
 */
@Injectable({
  providedIn: 'root'
})
export class LinkService {
  public constructor(
    private readonly store: Store,
    @Inject(APP_BASE_HREF) private readonly baseHref: string,
    @Inject(WINDOW) private readonly window: Window
  ) {}

  public getPreconfigLink(vehicleClass?: string, vehicleBody?: string, baumuster?: string): Observable<string> {
    return this.getLink(AppRoutePath.Preconfig, { vehicleClass, vehicleBody, baumuster }, !!baumuster);
  }

  public getVehicleLengthLink(vehicleClass?: string, vehicleBody?: string, baumuster?: string): Observable<string> {
    return this.getLink(AppRoutePath.VehicleLength, { vehicleClass, vehicleBody, baumuster }, !!baumuster);
  }

  public getMotorizationLink(vehicleClass?: string, vehicleBody?: string, baumuster?: string): Observable<string> {
    return this.getLink(AppRoutePath.Motorization, { vehicleClass, vehicleBody, baumuster }, !!baumuster);
  }

  /**
   * Link to Configuration page or a specific Configuration Step page (hint: configuration step is identified by category id from selectables)
   */
  public getConfigurationLink(vehicleId?: string, configurationStep?: string): Observable<string> {
    return this.getLink(AppRoutePath.Configuration, { vehicleId, configurationStep: configurationStep?.toLowerCase() });
  }

  /**
   * Link to a specific Configuration Step page (hint: configuration step is identified by category id from selectables)
   */
  public getConfigurationStepLink(configurationStep: string): Observable<string> {
    return this.getLink(AppRoutePath.Configuration, { configurationStep: configurationStep?.toLowerCase() });
  }

  public getConfigurationSummaryLink(vehicleId: string): Observable<string> {
    return this.getConfigurationLink(vehicleId, URL_PATH_SUMMARY.toLowerCase());
  }

  /**
   * Link to page matching given routeId
   */
  public getLink(routeId: AppRoutePath, pathParamValues?: Data, isBaumusterEntry = false): Observable<string> {
    return this.getLinkArray(routeId, pathParamValues, isBaumusterEntry).pipe(
      map((path: string[]) => withLeadingSlash(slashJoin(...path)))
    );
  }

  public getAbsoluteLink(routeId: AppRoutePath, pathParamValues?: Data): Observable<string> {
    return this.getLink(routeId, pathParamValues).pipe(
      map(path => slashJoin(this.window.location.origin, this.baseHref, path, this.window.location.search || ''))
    );
  }

  /**
   * Link to page matching given routeId and return as array
   */
  public getLinkArray(routeId: AppRoutePath, pathParamValues?: Data, isBaumusterEntry = false): Observable<string[]> {
    switch (routeId) {
      case AppRoutePath.Doorway: {
        return of([AppRoutePath.Doorway]);
      }
      case AppRoutePath.Preconfig:
      case AppRoutePath.Motorization:
      case AppRoutePath.VehicleLength:
      case AppRoutePath.Configuration:
      case AppRoutePath.OnlineCode: {
        return this.combinePathParams(routeId, pathParamValues, isBaumusterEntry);
      }
      default: {
        throw new Error(`Can not generate link for unknown route "${routeId}"`);
      }
    }
  }

  /**
   * Combine path parameters for given routeId.
   * Fallback to current context values, if respective param is not given.
   *
   * TODO: if routeId is configuration, but vehicleId is not set in contextService, this will never resolve!!!
   */
  private combinePathParams(
    routeId: AppRoutePath,
    pathParamValues: Data,
    isBaumusterEntry: boolean
  ): Observable<string[]> {
    pathParamValues = pathParamValues || {};

    /**
     * Note: this fn is quite ugly (as this needs to be extended with every new property) and only a temporary solution to get rid of the context service.
     */
    const getContextParameter = (key: AppRoutePathParam): Observable<string> =>
      this.store.select(ROUTE_PATH_PARAM_TO_SELECTOR_MAP[key] as (state: object) => string);

    const getParamValues = (...keys: AppRoutePathParam[]): Observable<string>[] =>
      keys.map<Observable<string>>(key =>
        pathParamValues[key]
          ? of(pathParamValues[key] as string)
          : getContextParameter(key).pipe(filterTruthy(), first())
      );

    const paramValues = isBaumusterEntry
      ? [...getParamValues(AppRoutePathParam.BM, AppRoutePathParam.Baumuster)]
      : [...getParamValues(AppRoutePathParam.VehicleClass, AppRoutePathParam.VehicleBody)];

    return combineLatest([
      of(routeId),
      ...getParamValues(AppRoutePathParam.Subprocess, AppRoutePathParam.Country, AppRoutePathParam.Language),
      ...(routeId === AppRoutePath.Motorization ||
      routeId === AppRoutePath.VehicleLength ||
      routeId === AppRoutePath.Preconfig
        ? paramValues
        : []),
      ...(routeId === AppRoutePath.Configuration
        ? [
            ...getParamValues(AppRoutePathParam.VehicleId),
            ...(pathParamValues.configurationStep
              ? [of((pathParamValues.configurationStep as string).toLowerCase())]
              : [])
          ]
        : []),
      ...(routeId === AppRoutePath.OnlineCode ? [...getParamValues(AppRoutePathParam.OnlineCode)] : [])
    ]);
  }
}
